import { useState } from 'react';
import { Link } from 'react-router';
import { CONTACT_TOPICS, MUNICIPALITIES } from '../../../shared/constants.ts';
import { api } from '../api.ts';
import { useAuth } from '../auth.tsx';
import { Container } from '../components/Layout.tsx';
import {
  Alert,
  Button,
  Card,
  ErrorMessage,
  Field,
  LinkButton,
  PageTitle,
  SelectField,
  TextArea,
} from '../components/ui.tsx';
import { useAction, useApi, useTitle } from '../hooks.ts';

interface PublicCourse {
  course: {
    title: string;
    hours: number;
    modules: { id: number; title: string; items: number; minutes: number | null }[];
  } | null;
  certified: number;
  organisation: string;
  signer: { name: string; title: string };
}

const STEPS = [
  {
    title: 'Skapa ett konto',
    text: 'Med din e-postadress. Du väljer kommun och skolform så att vi kan följa kursen i länet.',
  },
  {
    title: 'Gå kursen i din egen takt',
    text: 'Korta filmer, texter, quiz, diskussioner och reflektioner. Aurora sparar var du är.',
  },
  {
    title: 'Träffa kollegor',
    text: 'Några träffar online och på plats där ni provar, diskuterar och delar erfarenheter.',
  },
  {
    title: 'Få ditt intyg',
    text: 'När alla obligatoriska delar är klara får du ett intyg från Luleå tekniska universitet.',
  },
];

const FAQ = [
  {
    q: 'Behöver jag kunna något om AI innan?',
    a: 'Nej. Kursen börjar från grunden: vad generativ AI är, hur den fungerar och varför den ibland har fel.',
  },
  {
    q: 'Hur lång tid tar kursen?',
    a: 'Ungefär 40 timmar totalt. Du bestämmer själv takten, och du kan logga ut och fortsätta där du slutade.',
  },
  {
    q: 'Måste jag vara med på träffarna?',
    a: 'Det står vid varje träff om den är obligatorisk. Kursträffarna läggs in i din kalender med ett klick.',
  },
  {
    q: 'Vem ser det jag skriver?',
    a: 'Kursens lärare ser dina inlämningar. Diskussionsinlägg ser de andra i kursomgången. Din AI-dagbok ser bara du.',
  },
];

/** Om Aurora: kursen, projektet och hur det går till. Visas utan inloggning. */
export function AboutAuroraPage() {
  useTitle('Om Aurora');
  const { user } = useAuth();
  const { data } = useApi<PublicCourse>('/api/public/course');
  const course = data?.course;
  return (
    <>
      <section className="aurora-sky overflow-hidden text-white">
        <div className="stars" />
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:py-20">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-aurora-400">Om Aurora</p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight sm:text-5xl">
            Framtidsredo klassrum med AI-litteracitet i hela Norrbotten
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-white/85">
            AURORA är ett projekt vid {data?.organisation ?? 'Luleå tekniska universitet'} för att ge så många
            som möjligt av länets lärare kunskap om AI: hur det fungerar, hur det kan användas i lärandet och
            vad det betyder för skolan och samhället.
          </p>
          {!user && (
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/registrera" className="bg-white !text-night-900 hover:!bg-night-100">
                Skapa konto och börja
              </LinkButton>
              <LinkButton
                to="/kontakt"
                variant="ghost"
                className="!text-white ring-1 ring-white/40 hover:!bg-white/10"
              >
                Kontakta oss
              </LinkButton>
            </div>
          )}
        </div>
      </section>

      <Container>
        <div className="grid gap-6 lg:grid-cols-[1fr_22rem]">
          <div className="space-y-6">
            <Card>
              <h2 className="text-2xl font-bold text-night-900">Kursen</h2>
              <p className="mt-2 text-muted">
                Cirka {course?.hours ?? 40} timmar i din egen takt, med korta inspelade föreläsningar, texter,
                quiz, diskussioner och reflektionsuppgifter, kombinerat med träffar online och på plats.
              </p>
              {course && course.modules.length > 0 && (
                <>
                  <h3 className="mt-6 font-bold text-night-800">Kursens moduler</h3>
                  <ol className="mt-2 space-y-2">
                    {course.modules.map((m, i) => (
                      <li key={m.id} className="flex items-baseline gap-3 rounded-xl bg-night-50 px-4 py-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-night-800 text-sm font-bold text-white">
                          {i + 1}
                        </span>
                        <span className="flex-1 font-semibold">{m.title}</span>
                        <span className="text-sm text-muted">{m.items} delar</span>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </Card>

            <Card>
              <h2 className="text-2xl font-bold text-night-900">Så går det till</h2>
              <ol className="mt-4 grid gap-4 sm:grid-cols-2">
                {STEPS.map((s, i) => (
                  <li key={s.title} className="rounded-xl bg-night-50 p-4">
                    <p className="text-sm font-semibold text-aurora-600">Steg {i + 1}</p>
                    <h3 className="font-bold text-night-900">{s.title}</h3>
                    <p className="mt-1 text-sm text-muted">{s.text}</p>
                  </li>
                ))}
              </ol>
            </Card>

            <Card>
              <h2 className="text-2xl font-bold text-night-900">Vanliga frågor</h2>
              <div className="mt-3 divide-y divide-night-100">
                {FAQ.map((f) => (
                  <details key={f.q} className="py-3">
                    <summary className="cursor-pointer font-semibold text-night-800">{f.q}</summary>
                    <p className="mt-2 text-muted">{f.a}</p>
                  </details>
                ))}
              </div>
              <p className="mt-4 text-sm">
                Hittar du inte svaret?{' '}
                <Link to="/kontakt" className="font-semibold text-night-700 underline">
                  Kontakta oss
                </Link>
                .
              </p>
            </Card>
          </div>

          <aside className="space-y-6">
            <Card>
              <h2 className="font-bold text-night-900">För vem?</h2>
              <p className="mt-2 text-sm text-muted">
                Lärare och annan pedagogisk personal i Norrbottens {MUNICIPALITIES.length - 1} kommuner, från
                förskola till vuxenutbildning.
              </p>
              <p className="mt-3 text-sm">
                <Link to="/norrbotten" className="font-semibold text-night-700 underline">
                  Se hur många som tagit intyget i varje kommun
                </Link>
                {data && data.certified > 0 && (
                  <span className="text-muted"> ({data.certified} hittills)</span>
                )}
              </p>
            </Card>
            <Card>
              <h2 className="font-bold text-night-900">Bakom kursen</h2>
              <p className="mt-2 text-sm text-muted">
                Kursen ges av {data?.organisation ?? 'Luleå tekniska universitet'}
                {data?.signer.name ? `. Projektledare är ${data.signer.name} (${data.signer.title})` : ''}.
              </p>
            </Card>
            <Card>
              <h2 className="font-bold text-night-900">Personuppgifter och tillgänglighet</h2>
              <ul className="mt-2 space-y-1 text-sm">
                <li>
                  <Link to="/integritet" className="text-night-700 underline">
                    Hur vi hanterar personuppgifter
                  </Link>
                </li>
                <li>
                  <Link to="/tillganglighet" className="text-night-700 underline">
                    Tillgänglighetsredogörelse
                  </Link>
                </li>
              </ul>
            </Card>
          </aside>
        </div>
      </Container>
    </>
  );
}

/** Kontaktformulär som mejlar kursens kontaktadress. Visas utan inloggning. */
export function ContactPage() {
  useTitle('Kontakt');
  const { user } = useAuth();
  const site = useApi<{ contactEmail: string; organisation: string }>('/api/site').data;
  const [form, setForm] = useState({
    name: user ? `${user.firstName} ${user.lastName}` : '',
    email: user?.email ?? '',
    topic: '',
    message: '',
    website: '',
  });
  const [sent, setSent] = useState(false);
  const send = useAction(() => api.post('/api/contact', form));
  const f = send.error?.fields ?? {};
  return (
    <Container>
      <PageTitle sub="Frågor om kursen, ditt konto eller intyget? Skriv till oss så svarar vi så snart vi kan.">
        Kontakt
      </PageTitle>
      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <Card>
          {sent ? (
            <div className="space-y-4">
              <Alert kind="success">Tack! Ditt meddelande är skickat. Vi svarar till {form.email}.</Alert>
              <button
                type="button"
                className="text-sm text-night-700 underline"
                onClick={() => {
                  setSent(false);
                  setForm({ ...form, message: '', topic: '' });
                }}
              >
                Skicka ett meddelande till
              </button>
            </div>
          ) : (
            <form
              className="space-y-4"
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                if (await send.run()) setSent(true);
              }}
            >
              {send.error && !Object.keys(f).length && <ErrorMessage error={send.error} />}
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Namn"
                  autoComplete="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  error={f.name}
                  required
                />
                <Field
                  label="E-postadress"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  error={f.email}
                  hint="Hit skickar vi svaret."
                  required
                />
              </div>
              <SelectField
                label="Vad gäller det?"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                error={f.topic}
                options={[
                  { value: '', label: 'Välj' },
                  ...CONTACT_TOPICS.map((t) => ({ value: t, label: t })),
                ]}
                required
              />
              <TextArea
                label="Meddelande"
                rows={7}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                error={f.message}
                required
              />
              {/* Fälla för robotar: dold för människor och skärmläsare. */}
              <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
                <label>
                  Webbplats
                  <input
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </label>
              </div>
              <p className="text-xs text-muted">
                Vi använder uppgifterna bara för att svara dig. Läs mer om{' '}
                <Link to="/integritet" className="underline">
                  personuppgifter
                </Link>
                .
              </p>
              <Button type="submit" pending={send.pending}>
                Skicka
              </Button>
            </form>
          )}
        </Card>
        <aside className="space-y-6">
          <Card>
            <h2 className="font-bold text-night-900">E-post</h2>
            <p className="mt-2 text-sm text-muted">Du kan också mejla oss direkt.</p>
            {site && (
              <a
                href={`mailto:${site.contactEmail}`}
                className="mt-2 inline-block font-semibold text-night-700 underline"
              >
                {site.contactEmail}
              </a>
            )}
          </Card>
          <Card>
            <h2 className="font-bold text-night-900">Glömt lösenordet?</h2>
            <p className="mt-2 text-sm text-muted">
              Du kan välja ett nytt lösenord själv, eller logga in med en länk via e-post.
            </p>
            <Link
              to="/glomt-losenord"
              className="mt-2 inline-block text-sm font-semibold text-night-700 underline"
            >
              Välj nytt lösenord
            </Link>
          </Card>
          <Card>
            <h2 className="font-bold text-night-900">Om kursen</h2>
            <p className="mt-2 text-sm text-muted">
              Vad kursen innehåller, vem den är för och hur det går till.
            </p>
            <Link to="/om" className="mt-2 inline-block text-sm font-semibold text-night-700 underline">
              Om Aurora
            </Link>
          </Card>
        </aside>
      </div>
    </Container>
  );
}
